import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CHAT_FILE_UPLOAD_ACTION,
  CHAT_FILE_UPLOAD_CANCEL,
  CHAT_FILE_UPLOAD_CAPTION_LABEL,
  CHAT_FILE_UPLOAD_CAPTION_PLACEHOLDER,
  CHAT_FILE_UPLOAD_CONFIRMATION_MESSAGE,
  CHAT_FILE_UPLOAD_DRAGDROP_ACCEPTED_FILES_AND_SIZE,
  CHAT_FILE_UPLOAD_DRAGDROP_DESCRIPTION,
  CHAT_FILE_UPLOAD_DRAGDROP_TITLE,
  CHAT_FILE_UPLOAD_SIZE_OVERRIDE,
  CHAT_FILE_UPLOAD_SIZE_WARNING,
} from "@/constants/ui";
import {
  ERROR_DESC_CANNOT_SEND_GROUP_MSG,
  ERROR_TITLE_GENERIC,
  ERROR_TITLE_SORRY,
  WEBMSG_API_GROUP_MSG_SEND_ERROR,
} from "@/constants/errors";
import { HardDriveUpload, XIcon } from "lucide-react";
import {
  selectConversations,
  selectCurrConversation,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { Button } from "@/components/ui/button";
import { Conversation } from "@/types/conversation";
import DragDropZone from "./FileUploadDialog/DragDropZone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import UploadedFilePreview from "./FileUploadDialog/UploadedFilePreview";
import { sendMessage } from "@/features/chat/chatSlice";
import { useState } from "react";
import { useToast } from "../ui/use-toast";

// `disabled` while the conversation's history loads — a send mid-load errors out, so the
// trigger is blocked alongside the text input.
const FileUploadDialog = ({ disabled = false }: { disabled?: boolean }) => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [fileCaption, setFileCaption] = useState<string>("");
  const [fileSizeWarning, setFileSizeWarning] = useState<boolean>(false);
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations: Conversation[] = useAppSelector(selectConversations);

  const clearFileUploadInputs = () => {
    setFileSizeWarning(false);
    setFileCaption("");
    setFile(null);
  };

  const handleSendMediaMessage = async () => {
    if (!file || currConversationIndex === null || disabled) {
      return;
    }

    const addressId = conversations[currConversationIndex].addressId;
    const address = conversations[currConversationIndex].address;
    const addressType = conversations[currConversationIndex].addressType;

    const msgResponse = await dispatch(
      sendMessage({
        address,
        addressId,
        addressType,
        message: fileCaption,
        file: file,
      })
    );

    if (sendMessage.rejected.match(msgResponse)) {
      const errorMessage = msgResponse.error.message;
      console.error(errorMessage);

      if (errorMessage === WEBMSG_API_GROUP_MSG_SEND_ERROR) {
        return toast({
          variant: "destructive",
          title: ERROR_TITLE_SORRY,
          description: ERROR_DESC_CANNOT_SEND_GROUP_MSG,
        });
      }

      return toast({
        variant: "destructive",
        title: ERROR_TITLE_GENERIC,
        description: errorMessage ?? "Failed to send attachment. Please try again.",
      });
    }

    clearFileUploadInputs();
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <HardDriveUpload size={20} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-baseline justify-between">
            <AlertDialogTitle>
              {CHAT_FILE_UPLOAD_DRAGDROP_TITLE}
            </AlertDialogTitle>
            <AlertDialogCancel
              className="p-2 border-none text-neutral-400"
              onClick={clearFileUploadInputs}
            >
              <XIcon size={18} />
            </AlertDialogCancel>
          </div>

          <AlertDialogDescription>
            {CHAT_FILE_UPLOAD_DRAGDROP_DESCRIPTION}
          </AlertDialogDescription>
          <br />
          {/* Initial dialog w/drag and drop region */}
          {!file && (
            <div className="flex flex-col gap-4">
              <DragDropZone
                setFile={setFile}
                setFileSizeWarning={setFileSizeWarning}
                clearFileUploadInputs={clearFileUploadInputs}
              />
              <AlertDialogDescription>
                {CHAT_FILE_UPLOAD_DRAGDROP_ACCEPTED_FILES_AND_SIZE}
              </AlertDialogDescription>
            </div>
          )}
          {/* File preview after file is uploaded to drag and drop */}
          {file && (
            <UploadedFilePreview
              file={file}
              onClick={clearFileUploadInputs}
            />
          )}
          <br />
        </AlertDialogHeader>
        {file && fileSizeWarning && (
          <div className="flex flex-col gap-4 text-sm">
            <p className="text-red-500">{CHAT_FILE_UPLOAD_SIZE_WARNING}</p>
            <p className="text-red-500">
              {CHAT_FILE_UPLOAD_CONFIRMATION_MESSAGE}
            </p>
            <AlertDialogFooter>
              <Button variant="outline" onClick={clearFileUploadInputs}>
                {CHAT_FILE_UPLOAD_CANCEL}
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-500/90"
                onClick={() => setFileSizeWarning(false)}
                disabled={file === null}
              >
                {CHAT_FILE_UPLOAD_SIZE_OVERRIDE}
              </Button>
            </AlertDialogFooter>
          </div>
        )}
        {file && !fileSizeWarning && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-start justify-center gap-2">
              <Label>{CHAT_FILE_UPLOAD_CAPTION_LABEL}</Label>
              <Input
                type="text"
                placeholder={CHAT_FILE_UPLOAD_CAPTION_PLACEHOLDER}
                onChange={(e) => setFileCaption(e.target.value)}
                value={fileCaption}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={clearFileUploadInputs}>
                {CHAT_FILE_UPLOAD_CANCEL}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleSendMediaMessage()}
                disabled={file === null}
              >
                {CHAT_FILE_UPLOAD_ACTION}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default FileUploadDialog;
